/**
 * GoogleAuthCallback - Handles Google OAuth callback
 * Extracts token from URL hash and authenticates user
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Radio, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();

  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invoke consuming the token twice
    if (processedRef.current) return;
    processedRef.current = true;

    const handleCallback = async () => {
      try {
        // The token is in the URL hash (after #)
        const token = window.location.hash.substring(1); // Remove the #

        if (!token) {
          throw new Error('No authentication token received');
        }

        // Establish the session in the SHARED auth context so the app is
        // authenticated immediately (no "sign in again" second click).
        const result = await loginWithToken(token);
        if (!result.success) {
          throw new Error(result.error || 'Authentication failed');
        }

        setStatus('success');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1200);
      } catch (err) {
        console.error('Google auth callback error:', err);
        setStatus('error');
        setError(err.message || 'Authentication failed');

        setTimeout(() => {
          navigate('/auth', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate, loginWithToken]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        {/* Logo */}
        <img
          src="/nlt-radio-logo.png"
          alt="NLT Radio"
          className="w-24 h-24 mx-auto mb-6 object-contain drop-shadow-[0_0_25px_rgba(0,240,255,0.25)]"
        />

        {status === 'processing' && (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 mx-auto mb-4 border-2 border-[#00F0FF] border-t-transparent rounded-full"
            />
            <h2 
              className="text-xl font-bold text-white mb-2"
              style={{ fontFamily: 'Unbounded' }}
            >
              Signing you in...
            </h2>
            <p className="text-[#A1A1AA]">
              Please wait while we complete your authentication
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 15 }}
            >
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
            </motion.div>
            <h2 
              className="text-xl font-bold text-white mb-2"
              style={{ fontFamily: 'Unbounded' }}
            >
              Welcome to NLT Radio!
            </h2>
            <p className="text-[#A1A1AA]">
              Redirecting you now...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h2 
              className="text-xl font-bold text-white mb-2"
              style={{ fontFamily: 'Unbounded' }}
            >
              Authentication Failed
            </h2>
            <p className="text-[#A1A1AA] mb-4">
              {error || 'Something went wrong. Please try again.'}
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="px-6 py-3 rounded-xl bg-[#00F0FF] text-[#050505] font-semibold hover:bg-[#00F0FF]/90 transition-colors"
            >
              Back to Sign In
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default GoogleAuthCallback;
